import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import {
  Alert,
  Button,
  ButtonLink,
  Card,
  ErrorState,
  ProgressBar,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { ValidationReportView } from '@/features/validation/ValidationReportView';
import { canProceed } from '@/features/validation/issues';
import { MAX_POLL_ATTEMPTS, useUploadReport } from '@/features/validation/useUploadReport';
import { formatCount } from '@/lib/format';

import { stepUrl } from './steps';
import { useOrderStep } from './useOrderStep';

/**
 * /order/review — what we found in the file.
 *
 * Data comes from `?u=` via GET /api/v1/uploads/{id}, which also doubles as the
 * poll target for the 202 path, so this one screen covers "still checking",
 * "checked", "we gave up" and "the request failed" without a second route.
 *
 * The acknowledgement ticks are component state on purpose. Everything that
 * represents *progress* lives in the URL, but a confirmation is not progress —
 * it is a statement the customer is making right now about what they just read,
 * and a reload should ask for it again rather than remember a tick from a
 * session they may not have finished reading.
 */
export function ReviewStep() {
  const { step, stepper, urlState } = useOrderStep();
  const navigate = useNavigate();
  const uploadId = urlState.upload;

  const { report, pending, attempts, gaveUp, isLoading, isError, error, retry } =
    useUploadReport(uploadId);

  const [acknowledged, setAcknowledged] = useState<ReadonlySet<string>>(() => new Set<string>());
  const toggleAcknowledge = useCallback((code: string, checked: boolean) => {
    setAcknowledged((previous) => {
      const next = new Set(previous);
      if (checked) next.add(code);
      else next.delete(code);
      return next;
    });
  }, []);

  const uploadAnother = stepUrl('upload', { ...urlState, upload: undefined });
  const rejected = report?.verdict === 'reject';
  const ready = report ? canProceed(report, acknowledged) : false;

  const action = rejected ? (
    // A reject offers exactly one road: a different file. There is deliberately
    // no disabled "Continue" next to it — a greyed-out button reads as "keep
    // trying", and no amount of trying makes an encrypted PDF printable.
    <ButtonLink to={uploadAnother} fullWidth>
      Upload a different file
    </ButtonLink>
  ) : report ? (
    <div className="flex flex-col gap-2">
      <Button fullWidth disabled={!ready} onClick={() => navigate(stepUrl('options', urlState))}>
        Continue to print options
      </Button>
      <ButtonLink to={uploadAnother} variant="ghost" fullWidth>
        Upload a different file
      </ButtonLink>
    </div>
  ) : undefined;

  return (
    <AppShell
      title={step.title}
      subtitle="Every file gets checked before we print it."
      progress={stepper}
      backTo={uploadAnother}
      backLabel="Back to upload"
      action={action}
    >
      {isError && error ? (
        <ErrorState error={error} onRetry={retry} title="We could not load your file check" />
      ) : gaveUp && pending ? (
        <Alert severity="warning" title="This is taking longer than it should" live>
          <p>
            We have been checking your file for a while and it has not finished. Nothing is lost —
            your file is still with us. Try again in a moment, or upload it once more.
          </p>
          <p className="text-ink-subtle">
            Checked {formatCount(attempts)} times over about{' '}
            {formatCount(Math.round((MAX_POLL_ATTEMPTS * 2) / 60))} minutes.
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <Button onClick={retry} fullWidth>
              Check again
            </Button>
            <ButtonLink to={uploadAnother} variant="secondary" fullWidth>
              Upload a different file
            </ButtonLink>
          </div>
        </Alert>
      ) : pending ? (
        <Card title="Your file">
          <div className="flex flex-col gap-3">
            {/* Indeterminate on purpose: the server tells us it is working and
                roughly how often to ask, but never how far along it is. A fake
                percentage that jumps to 100 is worse than an honest "working". */}
            <ProgressBar label="Checking your file" />
            <p className="text-sm text-ink-muted">
              {pending.message ?? 'We are checking your file. This usually takes a few seconds.'}
            </p>
            {typeof pending.page_count_estimate === 'number' ? (
              <p className="text-sm text-ink-subtle">
                About {formatCount(pending.page_count_estimate)} pages to check.
              </p>
            ) : null}
          </div>
        </Card>
      ) : isLoading || !report ? (
        <SkeletonGroup label="Loading your file check">
          <div className="flex flex-col gap-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </SkeletonGroup>
      ) : (
        <ValidationReportView
          report={report}
          acknowledged={acknowledged}
          onToggleAcknowledge={toggleAcknowledge}
        />
      )}
    </AppShell>
  );
}
